import { useCallback, useEffect, useMemo } from 'react';
import {
  LinkTrackingData,
  TrackingDataEvent,
} from '../../../../../../common/AnalyticsContext/LinkTracking';
import { MouseButton } from '../../../../../../common/constants';
import { useBraze } from '../../../../../../common/vendors/braze/useBraze';
import { PrivacyInfo } from '../../../../../PrivacyInfo/PrivacyInfo';
import { SearchResultAd } from '../../../../../types';
import { Card } from '../card.styles';
import {
  ButtonContainer,
  Byline,
  Copy,
  DisplayAd,
  Headline,
  Image,
  ImageExpander,
  Logo,
  SmallPrint,
  StyledButton,
  StyledLink,
} from './SearchAdCard.styles';

export const SearchAdCard = ({ ad }: { ad: SearchResultAd }) => {
  const braze = useBraze();

  useEffect(() => {
    if (braze) braze.logCardImpressions([ad.originalCard], true);
  }, [ad.originalCard, braze]);

  const handleAdClick = useCallback(
    ({ button }) => {
      if (button !== MouseButton.LEFT && button !== MouseButton.MIDDLE) return;
      if (braze) braze.logCardClick(ad.originalCard, true);
    },
    [ad.originalCard, braze],
  );

  const trackingData: LinkTrackingData = useMemo(
    () => ({
      event: TrackingDataEvent.DISPLAY_AD,
      displayAdType: 'external',
      partnerName: ad.brandName,
    }),
    [ad.brandName],
  );

  return (
    <Card>
      <DisplayAd>
        <StyledLink
          trackingData={trackingData}
          onMouseDown={handleAdClick}
          to={ad.url ?? '#'}
          target="_blank"
        />

        <Image src={ad.images.url} title="ad image" role="img">
          <ImageExpander />
        </Image>

        <Copy>
          <div>
            <Logo src={ad.logoUrl} alt={ad.brandName} />
          </div>

          <PrivacyInfo brandName={ad.brandName} />

          <Headline>{ad.title}</Headline>

          <Byline>{ad.description}</Byline>

          {ad.smallPrint && <SmallPrint>{ad.smallPrint}</SmallPrint>}

          <ButtonContainer>
            <StyledButton
              trackingData={trackingData}
              color={ad.brandColor}
              to={ad.url ?? '#'}
              target="_blank"
              onMouseDown={handleAdClick}
            >
              {ad.linkText}
            </StyledButton>
          </ButtonContainer>
        </Copy>
      </DisplayAd>
    </Card>
  );
};
